import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { TooltipV2 } from '@acorns/web-components'
import styled from 'styled-components'

export type StepProps = {
  bgColor: string
  isChecked: boolean
  label?: string
  desc?: string
  tooltip?: string
  responsive: boolean
  complete?: boolean
  className?: string
  current?: string
}

const Container = styled.div<Partial<StepProps>>`
  align-items: left;
  background-color: ${props => props.bgColor};
  border-radius: 50%;
  display: flex;
  justify-content: left;
  margin-bottom: 50px;
  position: relative;
  width: 22px;
  height: 22px;
  z-index: 2;

  ${props =>
    props.responsive &&
    `
    @media screen and (max-width: ${props.theme.breakpoints.sm}px) {
      height: 16px;
      width: 16px;
    }
  `};
`

const Dot = styled.div<Partial<StepProps>>`
  background-color: ${props => props.theme.colors.white};
  border-radius: 50%;
  height: 14px;
  width: 14px;
  position: absolute;
  left: 4px;
  top: 4px;
  ${props =>
    props.responsive &&
    `
    @media screen and (max-width: ${props.theme.breakpoints.sm}px) {
      height: 6px;
      width: 6px;
    }
`};
`

const TextContainer = styled.span`
  display: flex;
  align-content: left;
  flex-direction: column;
  left: 20px;
  margin-left: 15px;
  position: relative;
`

const Label = styled.div<Partial<StepProps>>`
  color: ${props => props.theme.colors.slate};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  margin-bottom: 5px;
  margin-right: 3px;
  position: relative;
  white-space: nowrap;
  @media (max-width: ${props => props.theme.breakpoints.sm}px) {
    font-size: 14px;
  }
`

const Desc = styled.span`
  color: ${props => props.theme.colors.slate};
  display: block;
  font-size: 12px;
  position: relative;
  width: 300px;
  white-space: normal;
  @media (max-width: ${props => props.theme.breakpoints.sm}px) {
    font-size: 12px;
  }
`

const IconContainer = styled.div`
  align-items: center;
  background-color: ${props => props.theme.colors.stoneLight}
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  position: relative;
  justify-content: center;
  text-align: center;
  top: 2px;
`
// Tooltip Web Component is not updated to support wrapping text so this is the override in web-app
// https://github.com/Acornsgrow/web-app/blob/5dfb84ef9c44660c773899afc7947cdad1caab8d/src/components/StatsBar/Stats.tsx#L101
const StyledTooltip = styled(TooltipV2)`
  > div:last-child {
    line-height: 16px;
    font-size: 12px;
    max-width: 188px;
    white-space: pre-wrap;
    @media screen and (max-width: ${props => props.theme.breakpoints.sm}px) {
      max-width: 208px;
    }
  }
`

const TooltipIcon = styled(AcornsIcon)`
  position: absolute;
  bottom: 2px;
  right: 4px;
`

const CheckmarkIcon = styled(AcornsIcon)`
  position: absolute;
`

const TooltipItem = ({ string }) => {
  return (
    <StyledTooltip content={string} trigger={'click'} maxWidth={275}>
      <IconContainer onClick={null}>
        <TooltipIcon icon={AcornsIcon.Icon?.Tooltip} height={8} />
      </IconContainer>
    </StyledTooltip>
  )
}

const PositionIndicator = ({ complete, current, isChecked, responsive }) => {
  if (isChecked || complete) {
    return <CheckmarkIcon icon={AcornsIcon.Icon?.CheckmarkGreen} height={22} />
  } else {
    if (current) {
      return <Dot responsive={responsive} />
    }
    return null
  }
}

const Step = ({
  complete,
  current,
  isChecked,
  label,
  desc,
  tooltip,
  bgColor,
  responsive,
  className,
}: StepProps) => (
  <Container {...{ bgColor, responsive }} className={className}>
    <PositionIndicator
      complete={complete}
      current={current}
      isChecked={isChecked}
      responsive={responsive}
    />
    <TextContainer>
      {label && (
        <Label>
          {label} {tooltip && <TooltipItem string={tooltip} />}
        </Label>
      )}
      {desc && <Desc>{desc}</Desc>}
    </TextContainer>
  </Container>
)

export { Step }
